/**
 * Service Worker registration and client-side message handling.
 *
 * - Registers (or re-uses) the service worker
 * - Listens for messages broadcast from the SW (e.g. cross-tab logout)
 * - Exposes a `postToSW` helper for sending messages to the SW
 */

/**
 * Post a message to the active service worker.
 */
export function postToSW(type, payload = {}) {
  navigator.serviceWorker?.controller?.postMessage({ type, payload });
}

function getSessionData() {
  const el = document.getElementById("session-data");
  return el ? JSON.parse(el.textContent) : null;
}

function hasSession() {
  return !!document.getElementById("session-data");
}

// ─── Notification message handler registry ───────────────────────────
// Maps notification message types from the SDK (e.g. "badge", "connection")
// to the callback that should handle them on the client side.
// If a type arrives that isn't in this map, a warning is logged and nothing
// happens on the UI.

/** @type {Record<string, (payload: unknown) => void>} */
const notificationMessageHandler = {
  badge: (payload) => {
    console.log("[Notification] Badge update:", payload);
    // TODO: update badge UI
  },
  connection: (payload) => {
    console.log("[Notification] Connection event:", payload);
    // TODO: handle connection state change
  },
};

function handleNotificationMessage(payload) {
  const handler = notificationMessageHandler[payload?.type];
  if (!handler) {
    console.warn(
      `[SW-Client] No handler for notification type: "${payload?.type}"`,
    );
    return;
  }
  handler(payload);
}

// ─── SW message handling ─────────────────────────────────────────────

/**
 * Handle incoming messages from the service worker.
 */
function onSwMessage(event) {
  const { type, payload } = event.data ?? {};
  console.log("[SW-Client] Received from SW:", type, payload);

  switch (type) {
    case "PONG": {
      console.log(`[SW-Client] SW version: ${payload?.version}`);
      // If there's no active session, tell the SW to clean up any
      // leftover SDK connection (e.g. after sign-out + page refresh).
      if (!hasSession()) {
        postToSW("LOGOUT");
      }
      break;
    }

    case "START_NOTIFICATION_SDK_INIT": {
      const sessionData = getSessionData();

      if (!sessionData?.jwt) {
        console.error("[SW-Client] Cannot initialize SDK:", { sessionData });
        break;
      }

      const config = {
        refreshToken: sessionData.jwt,
        gatewayUrl: sessionData.gatewayUrl,
        hostApp: sessionData.brand,
        showDebugInfo: true,
        messageId: "",
      };

      console.log("[SW-Client] Sending SDK config to SW:", config);
      postToSW("INIT_NOTIFICATION_SDK", config);
      break;
    }

    case "NOTIFICATION": {
      handleNotificationMessage(payload);
      break;
    }

    case "LOGOUT_BROADCAST":
      console.log("[SW-Client] Another tab logged out — handle logout here");
      // TODO: redirect to login, clear local state, etc.
      break;

    default:
      console.log(`[SW-Client] Unhandled SW message type: ${type}`);
  }
}

/**
 * Register the service worker if not already active, and wire up
 * the message listener.
 */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.warn("[SW-Client] Service Workers not supported in this browser");
    return;
  }

  try {
    const registration =
      await navigator.serviceWorker.register("/roadrunner-sw.js");

    if (registration.installing) {
      console.log("[SW-Client] Service Worker installing…");
    } else if (registration.waiting) {
      console.log("[SW-Client] Service Worker installed, waiting to activate");
    } else if (registration.active) {
      console.log("[SW-Client] Service Worker active");
    }

    // Listen for messages from the SW.
    navigator.serviceWorker.addEventListener("message", onSwMessage);

    // When the SW is updated mid-session (skipWaiting activates a new SW),
    // the controller changes and the new SW loses the SDK instance.
    // Directly re-send the SDK config so the new SW can reinitialise it.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.log("[SW-Client] SW controller changed — re-initialising SDK");
      onSwMessage({ data: { type: "START_NOTIFICATION_SDK_INIT" } });
    });

    // Always send a PING so the SW can verify whether the SDK is initialised.
    // The SW will only respond with START_NOTIFICATION_SDK_INIT when the SDK
    // instance is missing (first install, SW update, or worker restart).
    // On regular page navigations where the SDK is already running, the SW
    // simply replies with PONG and does nothing else.
    //
    // NOTE: postToSW uses navigator.serviceWorker.controller, which can be null
    // on the first load of a page even when the SW is active (the SW only becomes
    // controller after clients.claim()). Using registration.active directly
    // ensures the PING always reaches the SW regardless of controller state.
    navigator.serviceWorker.ready.then((reg) => {
      reg?.active?.postMessage({ type: "PING", payload: {} });
    });
  } catch (err) {
    console.error("[SW-Client] Registration failed:", err);
  }
}
